import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
	Action,
	NgxsAfterBootstrap,
	NgxsOnInit,
	Selector,
	State,
	StateContext,
	Store,
} from '@ngxs/store';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
	LeapStateModel,
	LeapCrossWindowMessage,
	LeapWindowType,
	LeapWindowAction,
	GoToUrlMessage,
	CrossWindowMessageDialog,
} from './leap.model';
import * as actions from './leap.actions';
import { combineLatest, fromEvent } from 'rxjs';
import { filter } from 'rxjs/operators';
import { NavigationService } from '@services/nav/nav.service';
import { AppState } from '@state/app/app.state';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { BusinessSettingsRedirectDialogComponent } from '../business-settings-redirect-dialog/business-settings-redirect-dialog.component';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { LeapApiService } from '../../../shared/api-repos/leap/leap-api.service';

export const leapWindowTypeStorageKey = 'leapWindowType';
export const leapCrossWindowMessageStorageKey = 'leapCrossWindowMessage';

const leapStateDefaults: LeapStateModel = {
	windowType: null,
	crossWindowMessage: null,
};

type Ctx = StateContext<LeapStateModel>;

@UntilDestroy()
@State<LeapStateModel>({
	name: 'leap',
	defaults: structuredClone(leapStateDefaults),
})
@Injectable()
export class LeapState implements NgxsOnInit, NgxsAfterBootstrap {
	private loadedFromLeap = false;
	@SelectSnapshot(SelectedMatter.id) matterId: string;

	constructor(
		private dialog: MatDialog,
		private store: Store,
		private navService: NavigationService,
		private leapApiService: LeapApiService,
	) {}

	@Selector([LeapState])
	static windowType(state: LeapStateModel): LeapWindowType | null {
		return state?.windowType ?? null;
	}

	@Selector([LeapState])
	static message(state: LeapStateModel): LeapCrossWindowMessage | null {
		return state?.crossWindowMessage ?? null;
	}

	ngxsOnInit(ctx: Ctx): void {
		// Check for any existing window type value saved in session storage
		const initialWindowTypeValue = sessionStorage.getItem(
			leapWindowTypeStorageKey,
		);
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		if (Object.values(LeapWindowType).includes(initialWindowTypeValue as any)) {
			ctx.dispatch(
				new actions.UpdateWindowType(initialWindowTypeValue as LeapWindowType),
			);
		} else {
			ctx.dispatch(new actions.UpdateWindowType(null));
		}

		// Listen for new messages and update state
		fromEvent<StorageEvent>(window, 'storage')
			.pipe(
				untilDestroyed(this),
				filter(
					(event) =>
						event.storageArea === localStorage &&
						event.key === leapCrossWindowMessageStorageKey,
				),
			)
			.subscribe((event) => {
				if (!this.loadedFromLeap) return;

				if (event.newValue === null || event.newValue === 'null') {
					ctx.patchState({ crossWindowMessage: null });
					return;
				}

				try {
					const message = JSON.parse(event.newValue) as LeapCrossWindowMessage;
					ctx.patchState({ crossWindowMessage: message });
				} catch (err) {
					console.error(`Error parsing cross window message: ${err}. `);
				}
			});

		// Handle new incoming messages
		combineLatest([
			this.store.select(LeapState.message),
			this.store.select(LeapState.windowType),
			this.store.select(AppState.loadedFromLeap),
		])
			.pipe(
				untilDestroyed(this),
				filter(
					([msg, windowType, loadedFromLeap]) =>
						!!msg && !!windowType && loadedFromLeap && msg.to === windowType,
				),
			)
			.subscribe(([message]) => {
				switch (message.action) {
					case LeapWindowAction.OPENING_MATTER_URL:
					case LeapWindowAction.GO_TO_URL: {
						const data = message.data as GoToUrlMessage['data'];
						if (
							!data.relatedMatterId ||
							data.relatedMatterId === this.matterId
						) {
							this.navService.goToUrl(
								data.url,
								data.params || undefined,
								data.extras || undefined,
							);
						}
						break;
					}
					default: {
						console.error(
							'Leap cross window message contains an unknown action',
							(message as LeapCrossWindowMessage).action,
						);
						break;
					}
				}

				// Clear the message after processing
				window.localStorage.removeItem(leapCrossWindowMessageStorageKey);
			});
	}

	ngxsAfterBootstrap(ctx?: StateContext<LeapStateModel>): void {
		const rawMessage = window.localStorage.getItem(
			leapCrossWindowMessageStorageKey,
		);

		if (rawMessage === null || rawMessage === 'null') {
			ctx.patchState({ crossWindowMessage: null });
			return;
		}

		try {
			const parsedMessage = JSON.parse(rawMessage) as LeapCrossWindowMessage;
			ctx.patchState({ crossWindowMessage: parsedMessage });
		} catch (err) {
			console.error(`Error parsing cross window message: ${err}.`);
		}
	}

	@Action(actions.UpdateWindowType)
	updateWindowType(ctx: Ctx, { windowType }: actions.UpdateWindowType): void {
		ctx.patchState({ windowType });
		window.sessionStorage.setItem(leapWindowTypeStorageKey, windowType);
	}

	@Action(actions.SendCrossWindowMessage)
	sendCrossWindowMessage(
		ctx: Ctx,
		{
			crossWindowMessage,
			useDialog,
			autoDelete = true,
		}: actions.SendCrossWindowMessage,
	): void {
		const stringifiedMessage = JSON.stringify(crossWindowMessage);
		localStorage.setItem(leapCrossWindowMessageStorageKey, stringifiedMessage);

		if (crossWindowMessage && autoDelete) {
			setTimeout(() => {
				if (
					localStorage.getItem(leapCrossWindowMessageStorageKey) ===
					stringifiedMessage
				) {
					localStorage.removeItem(leapCrossWindowMessageStorageKey);
				}
			}, 5000);
		}

		switch (useDialog) {
			case CrossWindowMessageDialog.BUSINESS_SETTINGS_REDIRECT_DIALOG: {
				// Open a dialog after redirecting to business settings
				this.dialog.open(BusinessSettingsRedirectDialogComponent, {
					minWidth: '800px',
					panelClass: 'no-padding',
				});
				break;
			}
			case null:
			case undefined: {
				break;
			}
			default: {
				console.error('Unknown dialog type', useDialog);
				break;
			}
		}
	}

	@Action(actions.DeleteCrossWindowMessage)
	deleteCrossWindowMessage(): void {
		localStorage.removeItem(leapCrossWindowMessageStorageKey);
	}
}
